import { useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";
import { changeDepositAmount, changeDepositFormStatus, clearDepositForm, depositIsConfirming, selectDepositAccount, selectDepositAmount, selectDepositFormStatus, selectDepositValidAmountCheck, setConfirmingDeposit, setDepositAccount } from "./depositSlice";
import { isLoggedIn, selectToken } from "../login/loginSlice";
import { changeSelectedBranch, loadAccountsByBank, selectAccounts, selectCurrentBranch } from "../manageAccounts/accountsSlice";
import { addTransaction, addTransactionPending, clearNewTransaction } from "../transactionList/transactionsSlice";

import { useNavigate } from "react-router-dom";

import './DepositForm.css';

import Sidebar from "../../components/sidebar/Sidebar";
import { loadBranches, selectBranches } from "../manageBranches/branchesSlice";
import { selectUser } from "../home/userSlice";
import Select from "react-select";

export default function DepositForm() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const token = useSelector(selectToken);

    const loggedIn = useSelector(isLoggedIn);
    const depositAccount = useSelector(selectDepositAccount);
    const depositAmount = useSelector(selectDepositAmount);
    const depositValidAmountCheck = useSelector(selectDepositValidAmountCheck);
    const depositFormStatus = useSelector(selectDepositFormStatus);
    const branches = useSelector(selectBranches);
    const selectedBranch = useSelector(selectCurrentBranch);
    
    const addingTransaction = useSelector(addTransactionPending);
    const confirmingDeposit = useSelector(depositIsConfirming);

    const accounts = useSelector(selectAccounts);
    let accountsOptions = [{
        value: "blank", label: "-- select --"
    }]
    Object.values(accounts).forEach(account => {
        if (selectedBranch === 'blank' || account.branchId === selectedBranch) {
            accountsOptions.push({
                value: account.accountId,
                label: `${account.username} | ${account.accountType} | **${account.accountId.slice(-4)}`
            }) 
        }
    })

    const userInfo = useSelector(selectUser);
    const userRole = userInfo.userRole;
    const bankId = userInfo.bankId;

    useEffect(() => {
        if (!loggedIn) {
            navigate(`../login`);
        }
    }, [loggedIn, navigate])

    useEffect(() => {
        if (userRole !== 'USER') {
            dispatch(loadBranches({bankId, token}));
            dispatch(loadAccountsByBank({bankId, token}));
        }
    }, [userRole, bankId, dispatch, token])

    useEffect(() => {
        dispatch(setConfirmingDeposit(false));
        dispatch(clearDepositForm());
        dispatch(clearNewTransaction());
    }, [dispatch]);

    const handleSelectedBranchChange = event => {
        dispatch(changeSelectedBranch(event.target.value));
    }

    const handleDepositAccountChange = option => {
        dispatch(setDepositAccount(option));
    }

    const handleDepositAmountChange = event => {
        const payload = {
            depositAmount: event.target.value,
            validAmountCheck: Number(event.target.value) || event.target.value === '0'
        }
        dispatch(changeDepositAmount(payload));
    }

    const handleDepositButtonClick = event => {
        event.preventDefault();

        if (depositAccount === 'blank') {
            dispatch(changeDepositFormStatus('Please select a deposit account'));
        } else if (!(depositAmount > 0) || typeof Number(depositAmount) !== 'number') {
            dispatch(changeDepositFormStatus('Deposits must be a number greater than 0'));
        } else {
            dispatch(setConfirmingDeposit(true));
        }
    }

    const handleCancelButtonClick = event => {
        event.preventDefault();
        dispatch(setConfirmingDeposit(false));
    }

    const handleSubmit = event => {
        event.preventDefault();

        const date = new Date();
        const year = date.getFullYear();
        const month = date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1;
        const day = date.getDate() + 1 < 10 ? `0${date.getDate() + 1}` : date.getDate() + 1;
        const hour = date.getHours() < 10 ? `0${date.getHours()}` : date.getHours();
        const minutes = date.getMinutes() < 10 ? `0${date.getMinutes()}` : date.getMinutes() ;
        const seconds = date.getSeconds() < 10 ? `0${date.getSeconds()}` : date.getSeconds();
        const transactionDate = `${year}-${month}-${day}T${hour}:${minutes}:${seconds}`;

        const description = `Teller Deposit`;
        const depositTransaction = {
            accountId: depositAccount.value,
            type: 'credit',
            description: description,
            amount: depositAmount,
            transactionDate: transactionDate,
        }

        dispatch(addTransaction({transaction: depositTransaction, token}));
        navigate(`../${bankId}/deposit/confirmation`);
    }

    return (
        <main>
            <Sidebar />
            <section className="deposit-form">
                <h2>{confirmingDeposit ? "Confirm Deposit" : "Deposit Money"}</h2>
                <form id="deposit">
                    {confirmingDeposit ? "" : 
                        <label>
                            <h3>Select Branch</h3>
                            <select name="select-branch" onChange={handleSelectedBranchChange} value={selectedBranch}>
                                <option id="blank" key="blank" value="blank">-- select --</option>
                                {branches.map(branch => {
                                    return <option id={branch.branchId} key={branch.branchId} value={branch.branchId}>{branch.branchName}</option>
                                })}
                            </select>
                        </label>}
                    {confirmingDeposit ? <p><span>Deposit Account:</span> {Object.values(accounts).map(account => account.accountId === depositAccount.value ? `${account.accountType} ${account.accountId}` : "")}</p> : 
                        <label htmlFor="deposit-account">
                            <h3>Deposit Account</h3>
                            <Select 
                                className="react-select-container"
                                classNamePrefix="react-select"
                                value={depositAccount}
                                onChange={handleDepositAccountChange}
                                options={accountsOptions}
                                placeholder="Select a deposit account..."
                                unstyled={true}
                            />
                        </label>}
                    {confirmingDeposit ? <p><span>Deposit Amount:</span> ${Number(depositAmount).toFixed(2)}</p>: 
                    <label htmlFor="deposit-amount">
                        <h3>Deposit Amount</h3>
                        <div className="amount-field">
                            <p className="dollar-sign">$</p>
                            <input name="deposit-amount" onChange={handleDepositAmountChange} value={depositAmount} ></input>
                        </div>
                        {depositValidAmountCheck ? "" : <p className="error">Deposit amount must be a number.</p>}
                    </label>}
                    <p className="error">{depositFormStatus}</p>
                    <div className="button-container">
                        {confirmingDeposit ? <button onClick={handleCancelButtonClick}>Cancel</button> : ""}
                        {confirmingDeposit ? <button onClick={handleSubmit} disabled={addingTransaction}>{addingTransaction ? "Processing Deposit..." : "Submit Deposit"}</button> : <button onClick={handleDepositButtonClick}>Deposit Money</button>}
                    </div>
                </form>
            </section>
        </main>
    )
}